import React from "react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

interface Props {
  serviceId: number;
  serviceName?: string;
  onApply?: () => void;
}

const IaaSDSP: React.FC<Props> = ({ serviceId, serviceName = "Interview-as-a-Service (IaaS)", onApply }) => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();

  const handleNavigateBack = () => {
navigate(-1);
  };
  return (
    <section className="bg-white pt-2 p-4 sm:pb-6 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto m-10 space-y-4">
        {/* Header */}
        <div>
          <p className="text-gray-700 text-base sm:text-lg">
            The 7hills Interview-as-a-Service (IaaS) allows clients to outsource technical interviews across industries, domains, and roles. It ensures faster, unbiased, and quality-driven assessments aligned with client job needs.
          </p>
        </div>

        {/* Program Mode */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Program Mode</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>Online interviews by certified SMEs</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>Client or 7hills-defined TAF (Technical Assessment Feedback) forms</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>Fully customizable for the industry, domain- or JD-specific evaluations</span>
            </li>
          </ul>
        </div>

        {/* Duration */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Duration</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Hourly or engagement-based model</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Feedback shared within agreed timelines (minimum 1 Day)</span>
            </li>
          </ul>
        </div>

        {/* Key Benefits */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Key Benefits</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Saves panel time and shortens hiring cycles</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Improves Talent Acquisition KPIs</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Reduces wrong hires and legal costs</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>No Service charge for Sanity, Hygiene and Extended (SHE) Check along with the Malpractice cases.</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Enhances hiring quality and brand image</span>
            </li>
          </ul>
        </div>

        {/* Feedback & Reporting */}
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Feedback & Reporting</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <span>STAR-rated feedback per interview</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <span>Candidate outcomes: Selected / Trainable / Advised / Rejected</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <span>Monthly ROI and KPI insights</span>
            </li>
          </ul>
        </div>

        {/* Certification */}
        <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-4 border border-cyan-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Certification</h2>
          <p className="text-gray-700">
            All interviews handled by 7hills-certified SMEs ensuring quality and consistency.
          </p>
        </div>

        {/* Financials */}
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg p-4 border border-red-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Financials</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span>Pay-per-interview (as per rate card)</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span>Available on weekdays, weekends, and holidays</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span>Monthly invoicing</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span>Volume discounts available</span>
            </li>
          </ul>
        </div>

        {/* Deliverables */}
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-slate-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Deliverables</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" />
              <span>Role-specific TAF form</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" />
              <span>1-hour technical interview (L1/L2)</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" />
              <span>STAR report and monthly metrics summary</span>
            </li>
          </ul>
        </div>

        {/* Evaluation Parameters */}
        <div className="bg-gradient-to-br from-lime-50 to-green-50 rounded-lg p-4 border border-lime-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Evaluation Parameters</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-lime-600 flex-shrink-0 mt-0.5" />
              <span>Technical Depth & Industry, Domain/Skill Fit</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-lime-600 flex-shrink-0 mt-0.5" />
              <span>Analytical & Communication Skills</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-lime-600 flex-shrink-0 mt-0.5" />
              <span>Role-specific Problem Solving</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-lime-600 flex-shrink-0 mt-0.5" />
              <span>Trainability & Cultural Fit</span>
            </li>
          </ul>
        </div>

        {/* Engagement Flow */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Engagement Flow</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <span>Define or receive TAF form</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <span>Interviews only for client-shortlisted resumes, whether shared by the client or 7hills.</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <span>7hills conducts interviews</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <span>Monthly summary & metrics delivered</span>
            </li>
          </ul>
        </div>

        {/* Terms & Conditions */}
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-4 border border-amber-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Terms & Conditions</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <span>Governed by client's hiring agreement and rate card</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <span>Confidentiality strictly maintained including the candidate's data.</span>
            </li>
            <li className="flex gap-2">
              <ChevronRightIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <span>Refunds/credits as per contract terms</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default IaaSDSP;
